import { useState, useRef, useEffect } from 'react';

/**
 * Hook que persiste os filtros de listagem em sessionStorage e os restaura
 * quando o usuário volta de uma página filha (ex: detalhe/edição).
 *
 * Na desmontagem da lista, salva para qual rota o usuário foi.
 * Na próxima montagem, verifica se a rota anterior era uma sub-rota da lista
 * (ex: veio de /clientes/editar/5 → restaura; veio do menu /editais → não restaura).
 *
 * Funciona com qualquer tipo de navegação de volta (navigate(-1), navigate('/rota'), browser back).
 *
 * @param {string} key - Chave única para identificar a página (ex: 'atas', 'obras')
 * @param {object} defaultFilters - Objeto com os campos e valores padrão dos filtros
 */
const useListFilterState = (key, defaultFilters, defaultPageSize = 10) => {
    const storageKey = `list_filters_${key}`;
    const leftToKey = `${storageKey}_leftTo`;

    const readSavedState = () => {
        try {
            const raw = sessionStorage.getItem(storageKey);
            if (raw) return JSON.parse(raw);
        } catch { /* ignore */ }
        return null;
    };

    const getLeftToPath = () => {
        try { return sessionStorage.getItem(leftToKey) || ''; } catch { return ''; }
    };

    const savedState = readSavedState();
    // Captura o path atual no momento da montagem
    const currentPath = window.location.pathname;
    const leftToPath = getLeftToPath();

    // Restaura somente se da última vez que saímos desta página fomos para uma rota filha
    const canRestore = !!savedState && leftToPath.startsWith(currentPath + '/');

    // Ref para detectar desmontagem real vs desmontagem do StrictMode (mesmo URL)
    const mountPathRef = useRef(currentPath);

    const [filters, setFilters] = useState(canRestore ? (savedState?.filters ?? defaultFilters) : defaultFilters);
    const [searchTerm, setSearchTerm] = useState(canRestore ? (savedState?.searchTerm ?? '') : '');
    const [isSearchActive, setIsSearchActive] = useState(canRestore ? (savedState?.isSearchActive ?? false) : false);
    const [pageSize, setPageSize] = useState(canRestore ? (savedState?.pageSize ?? defaultPageSize) : defaultPageSize);

    // Na desmontagem: salva destino (só se for uma navegação real, não StrictMode)
    useEffect(() => {
        return () => {
            const destination = window.location.pathname;
            // StrictMode desmonta o componente sem mudar a URL — ignorar esses casos
            if (destination !== mountPathRef.current) {
                try { sessionStorage.setItem(leftToKey, destination); } catch { /* ignore */ }
            }
        };
    }, [leftToKey]);

    // Persiste mudanças nos filtros de volta ao storage
    useEffect(() => {
        try {
            sessionStorage.setItem(storageKey, JSON.stringify({ filters, searchTerm, isSearchActive, pageSize }));
        } catch { /* ignore */ }
    }, [filters, searchTerm, isSearchActive, pageSize, storageKey]);

    const clearPersistedState = () => {
        try { sessionStorage.removeItem(storageKey); } catch { /* ignore */ }
    };

    // hasRestoredFilters: verifica em tempo de execução do effect se há filtros para restaurar
    const hasRestoredFilters = () => {
        if (!leftToPath.startsWith(currentPath + '/')) return false;
        const state = readSavedState();
        if (!state) return false;
        const filtersHasValue = state.filters && Object.values(state.filters).some(v => v !== '');
        const searchHasValue = state.searchTerm && state.searchTerm.trim() !== '';
        return filtersHasValue || searchHasValue;
    };

    return {
        filters,
        setFilters,
        searchTerm,
        setSearchTerm,
        isSearchActive,
        setIsSearchActive,
        pageSize,
        setPageSize,
        hasRestoredFilters,
        clearPersistedState,
    };
};

export default useListFilterState;
